import React, { useEffect, useRef, useState } from 'react'

import {
    Button,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    TextField,
    useTheme,
} from '@material-ui/core'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import EditIcon from '@material-ui/icons/Edit'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import NumberFormat from 'react-number-format'

import { SelectorPopover } from '../../../../../components/TemplateScheduleGantt/Components/SelectorPopover'
import { usePopover } from '../../../../../hooks/usePopover'
import {
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceParams,
    DeleteTemplateServiceVendorParams,
    UpdateTemplateServiceParams,
    UpdateTemplateServiceVendorParams,
} from '../../../../../hooks/useEzSchedule/types'
import {
    getServiceImageDetails,
    ListVendor,
    TemplateService,
} from '../../../../../models'
import { FIT_SERVICE_ROW_HEIGHT } from './constants'
import { useWalkScheduleStyles } from './styles'

interface ScopeRow {
    area_count: number
    unit_count: number
    estimated_cost?: number
}

interface Props {
    templateService: TemplateService
    index: number
    serviceCount: number
    compact: boolean
    vendorList: ListVendor[]
    scope?: ScopeRow
    averageByVendorId: Record<number, number | null>
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    onAssignUnits: (templateService: TemplateService) => void
}

const BudgetField = (props: React.ComponentProps<typeof TextField>) => (
    <TextField {...props} size="small" />
)

const formatMoney = (amount: number) =>
    amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    })

const formatBudgetInput = (budget: number | string | null | undefined) => {
    if (budget == null || budget === '') return ''
    const amount = Number(budget)
    if (!Number.isFinite(amount) || amount === 0) return ''
    return String(amount)
}

export const WalkServiceLane = (props: Props) => {
    const {
        templateService,
        index,
        serviceCount,
        compact,
        vendorList,
        scope,
        averageByVendorId,
        addTemplateServiceVendor,
        deleteTemplateService,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        updateTemplateService,
        onAssignUnits,
    } = props
    const classes = useWalkScheduleStyles()
    const theme = useTheme()
    const vendorPopover = usePopover()
    const actionsRef = useRef<HTMLButtonElement>(null)
    const [serviceMenu, setServiceMenu] = useState<HTMLElement | null>(null)
    const [vendorMenu, setVendorMenu] = useState<{
        anchor: HTMLElement
        vendor: TemplateService['template_service_vendors'][number]
    } | null>(null)
    const [budgetOpen, setBudgetOpen] = useState(false)
    const [budgetText, setBudgetText] = useState(() =>
        formatBudgetInput(templateService.budget),
    )
    const pending = templateService.id < 0

    useEffect(() => {
        setBudgetText(formatBudgetInput(templateService.budget))
    }, [templateService.id, templateService.budget])

    const commitBudget = () => {
        if (pending) return
        const trimmed = budgetText.trim()
        const next = trimmed === '' ? 0 : Number(trimmed)
        if (!Number.isFinite(next) || next < 0) {
            setBudgetText(formatBudgetInput(templateService.budget))
            return
        }
        const rounded = Math.round(next * 100) / 100
        setBudgetText(formatBudgetInput(rounded))
        if (rounded !== Number(templateService.budget)) {
            updateTemplateService({
                templateServiceId: templateService.id,
                budget: rounded,
                stand_alone: templateService.stand_alone,
            })
        }
        setBudgetOpen(false)
    }

    const vendorOptions = vendorList.filter((vendor) => {
        const offersService = vendor.services.some(
            (service) => service.id === templateService.service.id,
        )
        const alreadyAdded = templateService.template_service_vendors.some(
            (row) => row.vendor.id === vendor.id,
        )
        return offersService && !alreadyAdded
    })

    const estimated = scope?.estimated_cost
    const variance =
        estimated == null ? null : Number(templateService.budget) - estimated
    const areaCount = scope?.area_count
    const unitCount = scope?.unit_count

    const openVendorPicker = () => {
        setServiceMenu(null)
        if (!actionsRef.current) return
        vendorPopover.handleOpen(({
            currentTarget: actionsRef.current,
        } as unknown) as React.MouseEvent<HTMLElement>)
    }

    return (
        <div>
            <div
                className={classes.serviceRow}
                style={{
                    height: compact ? FIT_SERVICE_ROW_HEIGHT : undefined,
                    background: templateService.stand_alone
                        ? theme.palette.action.hover
                        : undefined,
                }}
            >
                <div className={classes.serviceRowMain}>
                    <img
                        alt=""
                        src={
                            getServiceImageDetails(
                                templateService.service.id,
                                templateService.service.service_category,
                            ).icon
                        }
                        style={{ height: 20, width: 20, flexShrink: 0 }}
                    />
                    <span className={classes.serviceName}>
                        {templateService.service.name}
                    </span>
                    {!compact && areaCount != null && unitCount != null && (
                        <span className={classes.serviceCounts}>
                            <strong>{areaCount}</strong>
                            {` ${areaCount === 1 ? 'area' : 'areas'} · `}
                            <strong>{unitCount}</strong>
                            {` ${
                                unitCount === 1 ? 'work order' : 'work orders'
                            }`}
                        </span>
                    )}
                    <Button
                        ref={actionsRef}
                        className={classes.actionsButton}
                        size="small"
                        variant="contained"
                        color="primary"
                        aria-label={`${templateService.service.name} actions`}
                        disabled={pending}
                        startIcon={<MoreVertIcon />}
                        onClick={(event) => setServiceMenu(event.currentTarget)}
                    >
                        Actions
                    </Button>
                </div>
                {!compact && (
                    <div className={classes.moneyRow}>
                        <div className={classes.moneyBlock}>
                            <div className={classes.moneyLabel}>Budget</div>
                            <div className={classes.moneyValue}>
                                {formatMoney(
                                    Number(templateService.budget) || 0,
                                )}
                            </div>
                        </div>
                        <div className={classes.moneyBlock}>
                            <div className={classes.moneyLabel}>Estimated</div>
                            <div className={classes.moneyValue}>
                                {estimated == null
                                    ? '—'
                                    : formatMoney(estimated)}
                            </div>
                        </div>
                        <div className={classes.moneyBlock}>
                            <div className={classes.moneyLabel}>Variance</div>
                            <div
                                className={classes.moneyValue}
                                style={{
                                    color:
                                        variance == null
                                            ? undefined
                                            : variance < 0
                                            ? theme.palette.error.main
                                            : theme.palette.success.dark,
                                }}
                            >
                                {variance == null ? '—' : formatMoney(variance)}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {templateService.template_service_vendors.map((vendor) => {
                const average = averageByVendorId[vendor.id]
                return (
                    <div key={vendor.id} className={classes.vendorRow}>
                        <span className={classes.vendorName}>
                            {vendor.vendor.name}
                        </span>
                        {average != null && (
                            <span className={classes.vendorMeta}>
                                {`${average.toFixed(1)} / day`}
                            </span>
                        )}
                        <IconButton
                            size="small"
                            aria-label={`${vendor.vendor.name} actions`}
                            disabled={vendor.id < 0}
                            onClick={(event) =>
                                setVendorMenu({
                                    anchor: event.currentTarget,
                                    vendor,
                                })
                            }
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </div>
                )
            })}
            <Menu
                anchorEl={serviceMenu}
                open={Boolean(serviceMenu)}
                onClose={() => setServiceMenu(null)}
            >
                {templateService.template_service_vendors.length > 1 && (
                    <MenuItem
                        onClick={() => {
                            setServiceMenu(null)
                            onAssignUnits(templateService)
                        }}
                    >
                        <ListItemIcon>
                            <MeetingRoomIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Assign Units</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={openVendorPicker}>
                    <ListItemIcon>
                        <PersonAddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Add vendor</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setBudgetText(formatBudgetInput(templateService.budget))
                        setServiceMenu(null)
                        setBudgetOpen(true)
                    }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit budget</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={index === 0}
                    onClick={() => {
                        updateTemplateService({
                            templateServiceId: templateService.id,
                            position: index - 1,
                        })
                        setServiceMenu(null)
                    }}
                >
                    <ListItemIcon>
                        <ArrowUpwardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Move up</ListItemText>
                </MenuItem>
                <MenuItem
                    disabled={index === serviceCount - 1}
                    onClick={() => {
                        updateTemplateService({
                            templateServiceId: templateService.id,
                            position: index + 1,
                        })
                        setServiceMenu(null)
                    }}
                >
                    <ListItemIcon>
                        <ArrowDownwardIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Move down</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        updateTemplateService({
                            templateServiceId: templateService.id,
                            stand_alone: !templateService.stand_alone,
                        })
                        setServiceMenu(null)
                    }}
                >
                    <ListItemIcon>
                        {templateService.stand_alone ? (
                            <LinkIcon fontSize="small" />
                        ) : (
                            <LinkOffIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {templateService.stand_alone
                            ? 'Set linked'
                            : 'Set stand alone'}
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        deleteTemplateService({
                            templateServiceId: templateService.id,
                        })
                        setServiceMenu(null)
                    }}
                >
                    <ListItemIcon>
                        <DeleteForeverIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        Remove {templateService.service.name}
                    </ListItemText>
                </MenuItem>
            </Menu>
            <Menu
                anchorEl={vendorMenu?.anchor ?? null}
                open={Boolean(vendorMenu)}
                onClose={() => setVendorMenu(null)}
            >
                <MenuItem
                    onClick={() => {
                        if (!vendorMenu) return
                        updateTemplateServiceVendor({
                            templateServiceVendorId: vendorMenu.vendor.id,
                            auto_invoice: !vendorMenu.vendor.auto_invoice,
                        })
                        setVendorMenu(null)
                    }}
                >
                    <ListItemText
                        primary={
                            vendorMenu?.vendor.auto_invoice
                                ? 'Turn off auto invoice'
                                : 'Turn on auto invoice'
                        }
                        secondary="Approved work is invoiced nightly."
                    />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (!vendorMenu) return
                        deleteTemplateServiceVendor({
                            templateServiceVendorId: vendorMenu.vendor.id,
                        })
                        setVendorMenu(null)
                    }}
                >
                    <ListItemIcon>
                        <DeleteForeverIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Remove vendor</ListItemText>
                </MenuItem>
            </Menu>
            <Popover
                open={budgetOpen}
                anchorEl={actionsRef.current}
                onClose={() => setBudgetOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 12,
                    }}
                >
                    <NumberFormat
                        customInput={BudgetField}
                        variant="outlined"
                        label="Budget"
                        thousandSeparator
                        prefix="$"
                        decimalScale={2}
                        allowNegative={false}
                        value={budgetText}
                        onValueChange={(values) => setBudgetText(values.value)}
                        onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === 'Enter') commitBudget()
                        }}
                        style={{ width: 140 }}
                    />
                    <Button
                        color="primary"
                        variant="contained"
                        size="small"
                        onClick={commitBudget}
                    >
                        Save
                    </Button>
                </div>
            </Popover>
            <SelectorPopover
                theme={theme}
                popoverState={vendorPopover}
                label="Select Vendor"
                data={vendorOptions}
                onSubmit={(vendorId) => {
                    if (pending) return
                    addTemplateServiceVendor({
                        templateServiceId: templateService.id,
                        vendorId,
                        vendorName:
                            vendorOptions.find(
                                (vendor) => vendor.id === vendorId,
                            )?.name ?? '',
                        auto_invoice: false,
                    })
                }}
            />
        </div>
    )
}
